import { Effect, HashMap, Logger } from 'effect';

// Constants
import { MESSAGES } from '@shared/constants';

// Utils
import { isArray } from '@shared/utils';

// Types
import { EffectLogLevel } from '@/types';

/**
 * Creates a logging Effect program with the specified messages and annotations
 *
 * @param options - Configuration object for the log program
 * @param options.message - Optional message(s) to log. Can be a single value or array of values
 * @param options.annotations - Optional annotations to add to the logs
 * @param options.logLevel - Optional explicit log level (overrides severity-based level)
 * @returns Effect program that logs the messages with annotations
 *
 */
export const captureEffectLogsSync = (
  options: {
    message?: unknown | readonly unknown[];
    annotations?: Record<string, unknown>;
    logLevel?: EffectLogLevel;
  } = {},
) => {
  let capturedLogs: Record<string, unknown> = {};
  const { message, annotations, logLevel = EffectLogLevel.ERROR } = options;

  // Create base program - if message is provided, use it, otherwise create empty log
  let program: Effect.Effect<void, never, never>;

  try {
    // Create the appropriate Effect log based on level
    // Handle both single message and array of messages
    const messages = isArray(message) ? message : [message];

    switch (logLevel) {
      case EffectLogLevel.TRACE:
        program = Effect.logTrace(...messages);
        break;
      case EffectLogLevel.DEBUG:
        program = Effect.logDebug(...messages);
        break;
      case EffectLogLevel.INFO:
        program = Effect.logInfo(...messages);
        break;
      case EffectLogLevel.WARNING:
        program = Effect.logWarning(...messages);
        break;
      case EffectLogLevel.ERROR:
        program = Effect.logError(...messages);
        break;
      case EffectLogLevel.FATAL:
        program = Effect.logFatal(...messages);
        break;
      default:
        program = Effect.log(...messages);
    }

    // Add annotations if provided
    if (annotations && Object.keys(annotations).length > 0) {
      program = program.pipe(Effect.annotateLogs(annotations));
    }

    // Create a custom logger that captures messages
    const capturingLogger = Logger.make(
      ({ logLevel, annotations, ...rest }) => {
        const formatMessage = {
          logLevel: logLevel.label,
          payload: HashMap.reduce(
            annotations,
            {} as Record<string, unknown>,
            (acc, value, key) => {
              acc[key] = value;

              return acc;
            },
          ),
          ...rest,
        };

        capturedLogs = formatMessage;
      },
    );

    const enhancedProgram = program.pipe(
      Effect.provide(Logger.replace(Logger.defaultLogger, capturingLogger)),
      // Effect.as(capturedLogs),
    );

    Effect.runSync(enhancedProgram.pipe(Effect.provide(Logger.structured)));

    return capturedLogs;
  } catch (error) {
    if (error instanceof Error)
      return {
        error: 'Failed to parse the log from Effect: ' + error.message,
        logLevel: EffectLogLevel.ERROR,
      };

    return {
      error: MESSAGES.GENERAL_ERROR,
      logLevel: EffectLogLevel.ERROR,
    };
  }
};

/**
 * Utility to suppress specific console.warn messages.
 * Useful for ignoring known, harmless warnings (e.g. "Abort error suppressed").
 */
export const suppressConsoleWarnings = (message: string) => {
  // eslint-disable-next-line no-console
  const originalWarn = console.warn;

  // eslint-disable-next-line no-console
  console.warn = function (...args) {
    const shouldSuppress = args.some(
      (arg) => typeof arg === 'string' && arg.includes(message),
    );

    if (!shouldSuppress) {
      originalWarn.apply(console, args);
    }
  };
};

export const suppressConsoleError = (message: string) => {
  // eslint-disable-next-line no-console
  const originalError = console.error;

  // eslint-disable-next-line no-console
  console.error = function (...args) {
    const shouldSuppress = args.some(
      (arg) => typeof arg === 'string' && arg.includes(message),
    );

    if (!shouldSuppress) {
      originalError.apply(console, args);
    }
  };
};
